import { gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import * as React from 'react';

import { logError } from '@/imports/logging/ClientLogger';

import { usePollOption_PollV2Fragment } from './graphql-types/usePollOption_PollV2Fragment';
import {
  usePollOptions_CreatePollResponse,
  usePollOptions_CreatePollResponseVariables,
} from './graphql-types/usePollOptions_CreatePollResponse';
import {
  usePollOptions_UpdatePollResponse,
  usePollOptions_UpdatePollResponseVariables,
} from './graphql-types/usePollOptions_UpdatePollResponse';

const usePollOption_PollResponseFragment = gql`
  fragment usePollOption_PollResponse on PollResponse {
    _id
    pollPost {
      _id
      responseCount
      options {
        _id
        responseCount
        responses {
          _id
          user {
            _id
          }
        }
      }
      currentUserResponse {
        _id
        optionId
      }
      currentUserCanViewPollResults
    }
  }
`;

const UPDATE_POLL = gql`
  mutation usePollOptions_UpdatePollResponse($optionId: String!) {
    updatePollResponseV2(input: { optionId: $optionId }) {
      data {
        _id
        ...usePollOption_PollResponse
      }
    }
  }
  ${usePollOption_PollResponseFragment}
`;

const CREATE_POLL = gql`
  mutation usePollOptions_CreatePollResponse($optionId: String!) {
    createPollResponseV2(input: { optionId: $optionId }) {
      data {
        _id
        ...usePollOption_PollResponse
      }
    }
  }
  ${usePollOption_PollResponseFragment}
`;

export const usePollOption: {
  (poll: usePollOption_PollV2Fragment): {
    vote: (options: { optionId: string }) => Promise<void>;
  };
  pollFragment: DocumentNode;
} = (
  poll: usePollOption_PollV2Fragment
): {
  vote: (options: { optionId: string }) => Promise<void>;
} => {
  const toast = useToast();

  const [updateVote] = useMutation<
    usePollOptions_UpdatePollResponse,
    usePollOptions_UpdatePollResponseVariables
  >(UPDATE_POLL);
  const [createVote] = useMutation<
    usePollOptions_CreatePollResponse,
    usePollOptions_CreatePollResponseVariables
  >(CREATE_POLL);

  const vote = React.useCallback(
    async (options: { optionId: string }): Promise<void> => {
      const { optionId } = options;

      try {
        if (!poll?.currentUserResponse?.optionId) {
          await createVote({
            variables: {
              optionId,
            },
            optimisticResponse: {
              createPollResponseV2: {
                __typename: 'CreatePollResponseOutput_V2',
                data: {
                  __typename: 'PollResponse',
                  _id: 'temp-id-create-vote',
                  pollPost: {
                    ...poll,
                    __typename: 'PollV2',
                    _id: 'temp-poll-post-id-create-vote',
                    responseCount: poll.responseCount + 1,
                    currentUserResponse: {
                      __typename: 'PollResponse',
                      _id: 'temp-poll-response-id-create-vote',
                      optionId,
                    },
                    currentUserCanViewPollResults: true,
                  },
                },
              },
            },
          });
        } else {
          await updateVote({
            variables: {
              optionId,
            },
            optimisticResponse: {
              updatePollResponseV2: {
                __typename: 'UpdatePollResponseOutput_V2',
                data: {
                  __typename: 'PollResponse',
                  _id: 'temp-id-update-vote',
                  pollPost: {
                    ...poll,
                    __typename: 'PollV2',
                    _id: 'temp-poll-post-id-update-vote',
                    currentUserResponse: {
                      __typename: 'PollResponse',
                      _id: 'temp-poll-response-id-update-vote',
                      optionId,
                    },
                    currentUserCanViewPollResults: false,
                  },
                },
              },
            },
          });
        }
      } catch (err) {
        logError(err);
        toast({
          description: `Failed to update poll`,
          status: 'error',
        });
      }
    },
    [poll, createVote, updateVote, toast]
  );

  return {
    vote,
  };
};

usePollOption.pollFragment = gql`
  fragment usePollOption_PollV2Fragment on PollV2 {
    _id
    responseCount
    currentUserCanViewPollResults
    options {
      _id
      description
      responseCount
      order
      responses {
        _id
        user {
          _id
        }
      }
    }
    currentUserResponse {
      _id
      optionId
    }
  }
`;
